/**
 * Künstlersozialabgabe (Deutschland): Jahresauswertung der Entgelte, die eine Agentur an selbständige Creator gezahlt hat.
 * Meldung an die Künstlersozialkasse bis 31. März des Folgejahres. Rein; liefert Liste und Summe, keine Rechtsberatung.
 *
 * Regeln (Stand 2026, vereinfacht und als Hinweis gekennzeichnet):
 * zählt: Honorare an natürliche Personen und Personengesellschaften ohne Haftungsbeschränkung (Einzelunternehmen, GbR),
 *        auch an Creator im Ausland, auch Sachleistungen (Warenwert bei Barter) mit ihrem Wert, auch Nebenkosten/Material;
 * zählt nicht: gesondert ausgewiesene Umsatzsteuer, gesondert ausgewiesene Reisekosten, Zahlungen an juristische Personen
 *        und bestimmte Personengesellschaften (GmbH, UG, AG, KG, GmbH & Co. KG, OHG, SE, KGaA, eG, Ltd. …);
 * maßgeblich ist das Jahr der Zahlung, nicht das Rechnungsdatum.
 */
import type { Cents, IsoDate } from "./types.js";

/** Abgabesätze je Jahr in Prozent (Bekanntmachung des BMAS); die Plattform kann sie überschreiben */
export const KSK_RATES: Readonly<Record<number, number>> = { 2023: 5.0, 2024: 5.0, 2025: 5.0, 2026: 4.9 };

export interface KskLine {
  description: string;
  netCents: Cents;
  /** provisionsfreie Auslage (Reisekosten, Material, Produkte) */
  commissionFree?: boolean;
  /** ausdrücklich: Honorar, Reisekosten oder Material; sonst aus Beschreibung und Kennzeichen abgeleitet */
  kind?: "fee" | "travel" | "material";
}

export interface KskPayment {
  talentId: string;
  talentName: string;
  /** Firma und Rechtsform, über die der Creator abrechnet (talents.company_name / legal_form) */
  companyName?: string | null;
  legalForm?: string | null;
  country?: string | null;
  invoiceNumber: string | null;
  /** Tag der Auszahlung an den Creator */
  paidOn: IsoDate;
  /** Positionen netto in der Währung der Rechnung (currency) */
  lines: readonly KskLine[];
  /** Währung der Rechnung; ohne Angabe Euro */
  currency?: string;
  /**
   * Kurs 1 Einheit der Rechnungswährung = x Euro (eingefrorener Kurs der Rechnung, sonst EZB-Kurs am Auszahlungstag).
   * Fehlt er bei einer Fremdwährung, zählt die Zahlung nicht mit und steht unter „Bitte prüfen“ (GELD-21).
   */
  eurRate?: number | null;
  /** Herkunft des Kurses: an der Rechnung eingefroren oder EZB-Referenzkurs am Auszahlungstag */
  eurRateSource?: "frozen" | "ecb" | null;
  /** Warenwert von Produkten, die der Creator statt oder neben Geld erhalten hat (Barter), in Euro-Cent */
  goodsValueCents?: Cents;
  /** Warenwert nicht mitzählen, nur unter „Bitte prüfen“ nennen (Grund), z. B. Gifting der Marke oder Direktweg (GELD-10) */
  goodsCheckOnly?: string;
  /**
   * Nicht zählen, nur diesen Text unter „Bitte prüfen“ nennen (PRAXIS-R2-3): z. B. Monate, die im bisherigen System ausgezahlt
   * wurden und nur als Stand übernommen sind (Auszahlungstag und Belege liegen dort). Der Text kommt fertig vom Aufrufer.
   */
  checkOnly?: string;
}

export interface KskRow {
  talentId: string;
  talentName: string;
  exempt: boolean;
  exemptReason: string | null;
  basisCents: Cents;
  travelCents: Cents;
  goodsCents: Cents;
  payments: number;
  /** Positionen, die als Reisekosten gelten, nur wegen ihrer Bezeichnung: bitte prüfen */
  toCheck: string[];
  /** Zahlungen in Fremdwährung (GELD-21): umgerechnet (mit Kurs) oder ohne Kurs nicht mitgezählt; Beträge netto in der Rechnungswährung */
  foreign: KskForeign[];
}

export interface KskForeign {
  invoiceNumber: string | null;
  paidOn: IsoDate;
  currency: string;
  /** Summe der Positionen netto in der Rechnungswährung (ohne Reisekosten) */
  netCents: Cents;
  /** Umgerechnet in Euro-Cent; null = kein Kurs, nicht mitgezählt */
  eurCents: Cents | null;
  rate: number | null;
  rateSource: "frozen" | "ecb" | null;
}

export interface KskReport {
  year: number;
  ratePercent: number | null;
  rows: KskRow[];
  totalBasisCents: Cents;
  levyCents: Cents | null;
  notes: string[];
}

const EXEMPT_FORMS: readonly [RegExp, string][] = [
  [/\bgmbh\s*&\s*co\.?\s*kg\b/i, "GmbH & Co. KG"],
  [/\bgmbh\b/i, "GmbH"],
  [/\bug\b|haftungsbeschränkt/i, "UG (haftungsbeschränkt)"],
  [/\bkgaa\b/i, "KGaA"],
  [/\bag\b|aktiengesellschaft/i, "AG"],
  [/\bse\b/i, "SE"],
  [/\bkg\b|kommanditgesellschaft/i, "KG"],
  [/\bohg\b/i, "OHG"],
  [/\be\.?\s?g\.?\b|genossenschaft/i, "eG"],
  [/\bltd\.?\b|limited|\binc\.?\b|\bllc\b|\bsarl\b|\bs\.?a\.?r\.?l\.?\b|\bb\.?v\.?\b/i, "Kapitalgesellschaft (Ausland)"],
];

/** Ist ein Creator als Zahlungsempfänger von der Abgabe ausgenommen (juristische Person oder bestimmte Personengesellschaft)? */
export function kskExemption(legalForm: string | null | undefined, companyName: string | null | undefined): { exempt: boolean; reason: string | null } {
  const text = `${legalForm ?? ""} ${companyName ?? ""}`.trim();
  if (!text) return { exempt: false, reason: null };
  for (const [re, name] of EXEMPT_FORMS) if (re.test(text)) return { exempt: true, reason: `Zahlung an ${name}: nicht abgabepflichtig` };
  return { exempt: false, reason: null };
}

// Wortgenau: „Reisekosten“, „Anfahrt“, „Hotel“ …, aber nicht „Reisefieber“ oder „Bahnhofsviertel“ als Kampagnentitel
const TRAVEL = /(^|[^a-zäöüß])(reisekosten|reise|fahrtkosten|fahrt|anfahrt|abfahrt|hin- und rückfahrt|bahnfahrt|bahnticket|bahn|zugticket|zug|flug|flugticket|hotel|übernachtung|uebernachtung|taxi|kilometer|km|km-pauschale|kilometerpauschale|mietwagen|parken|parkgebühr|spesen|verpflegung|verpflegungspauschale)(?![a-zäöüß])/i;

function lineKind(l: KskLine): { kind: "fee" | "travel" | "material"; guessed: boolean } {
  if (l.kind) return { kind: l.kind, guessed: false };
  if (TRAVEL.test(l.description)) return { kind: "travel", guessed: true };
  return { kind: l.commissionFree ? "material" : "fee", guessed: false };
}

/** Jahresauswertung. Zahlungen außerhalb des Jahres werden ignoriert; Beträge netto in Euro-Cent. */
/** `lang`: Sprache der Hinweise (Oberflächensprache der Agentur, SICHT-AGENTUR-REST-25); Zahlen und Zeilen sind gleich. */
export function kskReport(payments: readonly KskPayment[], year: number, rates: Readonly<Record<number, number>> = KSK_RATES, lang: "de" | "en" = "de"): KskReport {
  const rows = new Map<string, KskRow>();
  for (const p of payments) {
    if (!p.paidOn.startsWith(`${year}-`)) continue;
    const ex = kskExemption(p.legalForm, p.companyName);
    const row = rows.get(p.talentId) ?? { talentId: p.talentId, talentName: p.talentName, exempt: ex.exempt, exemptReason: ex.reason,
      basisCents: 0, travelCents: 0, goodsCents: 0, payments: 0, toCheck: [], foreign: [] };
    if (p.checkOnly) {
      row.toCheck.push(p.checkOnly);
      rows.set(p.talentId, row);
      continue;
    }
    if (p.goodsCheckOnly) {
      if (p.goodsValueCents) row.toCheck.push(`Warenwert ${(p.goodsValueCents / 100).toFixed(2).replace(".", ",")} € (${p.paidOn.slice(5, 7)}/${p.paidOn.slice(0, 4)}) nicht gezählt: ${p.goodsCheckOnly}`);
      rows.set(p.talentId, row);
      continue;
    }
    // Fremdwährung (GELD-21): die Meldung ist in Euro; umgerechnet mit dem Kurs der Rechnung bzw. des Auszahlungstags, ohne Kurs nicht gezählt
    const currency = (p.currency ?? "EUR").toUpperCase();
    const foreign = currency !== "EUR";
    const rate = foreign ? (p.eurRate != null && Number.isFinite(p.eurRate) && p.eurRate > 0 ? p.eurRate : null) : 1;
    const eur = (c: Cents): Cents => (foreign ? Math.round(c * rate!) : c);
    if (foreign && rate === null) {
      const fees = p.lines.filter((l) => lineKind(l).kind !== "travel").reduce((a, l) => a + l.netCents, 0);
      row.foreign.push({ invoiceNumber: p.invoiceNumber, paidOn: p.paidOn, currency, netCents: fees, eurCents: null, rate: null, rateSource: null });
      rows.set(p.talentId, row);
      continue;
    }
    row.payments += 1;
    let fees = 0;
    for (const l of p.lines) {
      const k = lineKind(l);
      if (k.kind === "travel") {
        row.travelCents += eur(l.netCents);
        if (k.guessed) row.toCheck.push(`${p.invoiceNumber ?? "ohne Nummer"}: ${l.description}`);
      } else {
        row.basisCents += eur(l.netCents);
        fees += l.netCents;
      }
    }
    if (foreign) row.foreign.push({ invoiceNumber: p.invoiceNumber, paidOn: p.paidOn, currency, netCents: fees, eurCents: eur(fees), rate, rateSource: p.eurRateSource ?? null });
    if (p.goodsValueCents) {
      row.goodsCents += p.goodsValueCents;
      row.basisCents += p.goodsValueCents;
    }
    rows.set(p.talentId, row);
  }
  const list = [...rows.values()].sort((a, b) => a.talentName.localeCompare(b.talentName, "de"));
  const totalBasisCents = list.filter((r) => !r.exempt).reduce((a, r) => a + r.basisCents, 0);
  const ratePercent = rates[year] ?? null;
  const notes = lang === "en" ? [
    "What counts is the year of the payout to the creator. VAT and separately shown travel costs are not included, benefits in kind (goods value) are.",
    "Payments to GmbH, UG, AG, KG, GmbH & Co. KG and OHG are not subject to the levy; payments to sole traders and GbR are, including creators abroad.",
    "For collaborations the creator bills directly to the brand, the brand is usually liable for the levy, not the agency.",
    "Note, not legal advice: please agree the return with your tax advisor.",
  ] : [
    "Maßgeblich ist das Jahr der Auszahlung an den Creator. Umsatzsteuer und gesondert ausgewiesene Reisekosten zählen nicht mit, Sachleistungen (Warenwert) schon.",
    "Zahlungen an GmbH, UG, AG, KG, GmbH & Co. KG und OHG sind nicht abgabepflichtig; bei Einzelunternehmen und GbR schon – auch bei Creatorn im Ausland.",
    "Bei Kooperationen, die der Creator direkt mit der Marke abrechnet, ist in der Regel die Marke abgabepflichtig, nicht die Agentur.",
    "Hinweis ohne Rechtsberatung: Die Meldung bitte mit der Steuerberatung abstimmen.",
  ];
  if (ratePercent === null) notes.unshift(lang === "en" ? `No levy rate is stored for ${year} yet; the total of fees is complete nonetheless.` : `Für ${year} ist noch kein Abgabesatz hinterlegt; die Summe der Entgelte ist trotzdem vollständig.`);
  return { year, ratePercent, rows: list, totalBasisCents, levyCents: ratePercent === null ? null : Math.round((totalBasisCents * ratePercent) / 100), notes };
}

/** Kooperationsmonat mit Warenwert (deal_periods.goods_value_cents) samt Weg, Modell und Stand */
export interface KskGoodsMonth {
  talentId: string;
  talentName: string;
  companyName?: string | null;
  legalForm?: string | null;
  country?: string | null;
  /** Leistungsmonat YYYY-MM oder YYYY-MM-DD */
  month: string;
  goodsValueCents: Cents;
  /** Modell der Kooperation (gifting, barter …) */
  dealModel: string;
  /** Wirksamer Abrechnungsweg */
  route: "via_agency" | "direct_to_brand";
  /** Monat oder Kooperation storniert */
  cancelled: boolean;
  /** geliefert bzw. abgerechnet (angefordert, abgerechnet, bezahlt, gebucht oder Arbeitsstand veröffentlicht und später) */
  delivered: boolean;
}

/**
 * Sachleistungen für die Auswertung (GELD-10): Warenwert zählt nur bei Monaten über die Agentur, die geliefert bzw.
 * abgerechnet und nicht storniert sind, im Leistungsmonat. Gifting (Produkte der Marke, nicht von der Agentur gewährt),
 * der Direktweg (in der Regel ist die Marke abgabepflichtig) und noch nicht gelieferte Monate stehen nur unter „Bitte prüfen“.
 */
export function kskGoodsPayments(months: readonly KskGoodsMonth[], year: number): KskPayment[] {
  const out: KskPayment[] = [];
  for (const g of months) {
    if (!(g.goodsValueCents > 0) || g.cancelled || !g.month.startsWith(`${year}-`)) continue;
    const check = g.route === "direct_to_brand" ? "Direktweg, in der Regel ist die Marke abgabepflichtig"
      : g.dealModel === "gifting" ? "Gifting, die Produkte stellt die Marke"
        : !g.delivered ? "Monat noch nicht geliefert" : null;
    out.push({ talentId: g.talentId, talentName: g.talentName, companyName: g.companyName ?? null, legalForm: g.legalForm ?? null, country: g.country ?? null,
      invoiceNumber: null, paidOn: `${g.month.slice(0, 7)}-01`, lines: [], goodsValueCents: g.goodsValueCents, ...(check ? { goodsCheckOnly: check } : {}) });
  }
  return out;
}
